import { defineStore } from 'pinia'
import propertyApi from '@/api/property'
import type { CancelablePromiseWrapper } from '@/utils'
import type {
  MarketSearchFilters,
  MarketComparablePayload,
  MarketStatsQuery,
  PropertyDetailsResponse,
  PropertySearchResponse,
  PropertyReportPropertyInsert,
  SimpleProperty
} from 'common'

const trackAbnormalLeaseSpikesForProperty = (property: PropertyDetailsResponse | null) => {
  if (!property?.abnormal_lease_spikes?.length) return
  if (import.meta.env.MODE === 'reports') return
  if (!import.meta.env.VITE_PENDO_API_KEY || !window.pendo?.track) return

  const spikeDates = property.abnormal_lease_spikes.map((spike) => spike.spike_date)
  window.pendo.track(`abnormal-lease-spike-${property.id}`, {
    property_id: property.id,
    num_spikes: spikeDates.length,
    spike_dates: spikeDates
  })
}

export const usePropertyStore = defineStore('property', {
  state: () => ({
    propertySearchCancelable: null as CancelablePromiseWrapper<PropertySearchResponse[]> | null
  }),
  actions: {
    propertySearch(
      q: string,
      filters: {
        lat?: number
        lon?: number
        zipCode?: string
        state?: string
        maxDistance?: number
      },
      cancelPrevious = false
    ) {
      if (cancelPrevious && this.propertySearchCancelable) {
        this.propertySearchCancelable.cancel()
        this.propertySearchCancelable = null
      }

      const promise = propertyApi
        .propertySearch(q, {
          lat: filters.lat,
          lon: filters.lon,
          state: filters.state,
          zipCode: filters.zipCode,
          maxDistance: filters.maxDistance
        })
        .then((results) => {
          this.propertySearchCancelable = null
          for (const result of results) {
            // We remove created_on because we don't want to use this anywhere.
            // This field was created for our API customers only.
            delete result.created_on
          }
          return results
        })

      this.propertySearchCancelable = promise
      return promise.unwrap()
    },
    async details(id: string) {
      const property = await propertyApi.details(id)
      trackAbnormalLeaseSpikesForProperty(property)
      return property
    },
    async detailsByIds(ids: string[]) {
      const properties = await propertyApi.detailsByIds(ids)
      for (const property of properties) {
        trackAbnormalLeaseSpikesForProperty(property)
      }
      return properties
    },
    async insertProperty(data: PropertyReportPropertyInsert) {
      return propertyApi.insertProperty(data)
    },
    async comparables(
      subject: PropertyDetailsResponse | SimpleProperty,
      params: {
        excludedIds: string[]
        selectedIds: string[]
        topN: number
        renovations?: number
        maxDistance?: number
        minNumberUnits?: number
        maxNumberUnits?: number
        minYearBuilt?: number
        maxYearBuilt?: number
        minNumberStories?: number
        maxNumberStories?: number
        useEmbedding?: boolean
        isAffordable?: boolean
        isBuildToRent?: boolean
        isSenior?: boolean
        isStudent?: boolean
        isCondo?: boolean
        isSingleFamily?: boolean
        isApartment?: boolean
        isLeaseUp?: boolean
        maxMilesToUniversity?: number
        maxMilesToHospital?: number
      }
    ) {
      return propertyApi.comparables(subject, params)
    },
    async marketComparables(filters: MarketComparablePayload) {
      return propertyApi.marketComparables(filters)
    },
    async marketStatistics(subject: MarketStatsQuery) {
      return propertyApi.marketStatistics(subject)
    },
    async marketShapeSearch(q: string) {
      return propertyApi.marketShapeSearch(q)
    },
    async shapeSearchByCentroid(
      tag:
        | 'MSA'
        | 'City'
        | 'County'
        | 'Zip Code'
        | 'Neighborhood'
        | 'School District'
        | 'University'
        | 'School'
        | 'Census Tract',
      lat: number,
      lon: number
    ) {
      return propertyApi.shapeSearchByCentroid(tag, lat, lon)
    },
    async marketDemographics(filters: MarketSearchFilters) {
      return propertyApi.marketDemographics(filters)
    }
  }
})
